<template>
  <div class="add-intent-modal">
    <modal @cancel="cancel()">
      <template #title>
        <p class="modal-card-title">Add a new intent</p>
      </template>

      <template v-slot:body>
        <div class="edit-title-container">
          <h1>Name</h1>
          <div class="field">
            <n-text-input
              class="edit-title"
              v-model="intentTitle"
              :hideLabel="true"
              label="Intent name"
              :disabled="isSaveInProgress"
              placeholder="Enter Intent Name"
              :errorMessage="errorMessage"
              @input="$v.intentTitle.$touch()"
            ></n-text-input>
          </div>
        </div>

        <div class="edit-utterances-container">
          <h1>Example Utterances</h1>
          <UtteranceList v-model="intentUtterances" term="utterance" :isEditable="!isSaveInProgress" />
        </div>
      </template>

      <template v-slot:buttons>
        <n-button-group>
          <n-button variant="primary" @click="saveIntent" :loading="isSaveInProgress">Save </n-button>
          <n-button @click="cancel" :disabled="isSaveInProgress" variant="primary--outline">Cancel</n-button>
        </n-button-group>
      </template>
    </modal>
  </div>
</template>

<script lang="ts" setup>
import Modal from '@/shared/components/Modal.vue';
import { nanoid } from 'nanoid';
import { required, maxLength } from '@vuelidate/validators';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import Toast from '@/shared/services/toast';
import { IntentViewModel } from '@/features/bots/models/intents/intentViewModel';
import { UtteranceViewModel } from '@/features/bots/models/intents/utteranceViewModel';
import UtteranceList from '@/features/bots/components/designer/UtteranceList.vue';
import { CREATE_INTENT } from '@/store/intents/actionTypes';
import { SaveIntentViewModel } from '../../models/intents/saveIntentViewModel';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

defineExpose({
  addNewIntent,
});

const store = useStore();
const createIntent = (intent: SaveIntentViewModel): Promise<IntentViewModel> => store.dispatch(CREATE_INTENT, intent);

const isSaveInProgress = ref(false);
const intent = ref<IntentViewModel>({} as IntentViewModel);
const intentId = ref('');
const intentTitle = ref('');
const intentUtterances = ref<UtteranceViewModel[]>([]);

const validations = {
  intentTitle: {
    required,
    maxLength: maxLength(50),
    intentNameValid: VuelidateUtil.intentName,
  },
  intentUtterances: {
    required,
  },
};

const $v = useVuelidate(validations, { intentTitle, intentUtterances });

const emit = defineEmits<{
  (e: 'completed', intent: IntentViewModel): void;
  (e: 'closed'): void;
}>();

function cancel() {
  $v.value.$reset();
  emit('closed');
}

async function saveIntent() {
  $v.value.$touch();
  if ($v.value.$invalid) {
    Toast.showError('Fix the errors in red before saving');
    return;
  }
  try {
    isSaveInProgress.value = true;
    intent.value.utterances = intentUtterances.value.sort((uttOne, uttTwo) => uttOne.text.localeCompare(uttTwo.text));
    intent.value.title = intentTitle.value.trim();
    intent.value = await createIntent({
      title: intent.value.title,
      utterances: intent.value.utterances.map((utterance) => utterance.text),
    });
    Toast.showSuccess('Saved intent');
    emit('completed', intent.value);
    $v.value.$reset();
  } catch (ex: any) {
    Toast.showError(ex.message);
  } finally {
    isSaveInProgress.value = false;
  }
}

function addNewIntent(firstUtterance?: string) {
  intent.value = {
    id: `intent-${nanoid(7)}`,
    title: '',
    utterances: [{ id: nanoid(7), text: firstUtterance ? firstUtterance : '' }],
  } as IntentViewModel;

  intentId.value = intent.value.id;
  intentTitle.value = intent.value.title;
  intentUtterances.value = intent.value.utterances;
}

const errorMessage = computed(() => {
  const intentTitle = $v.value.intentTitle;
  if (!intentTitle.$dirty) {
    return '';
  }
  if (intentTitle.required.$invalid) {
    return 'Name must not be empty';
  } else if (intentTitle.intentNameValid.$invalid) {
    return 'Name must not contain <, >, *, %, &, :, \\, $';
  } else if (intentTitle.maxLength.$invalid) {
    return 'Name must not be longer than 50 characters';
  }

  return '';
});
</script>

<style scoped lang="scss">
.add-intent-modal {
  visibility: visible;
}
.field {
  padding: 0.25rem 0.5rem;
}

.edit-title {
  width: 100%;
  height: 45px;
  background: white;
}

.edit-utterances-container {
  margin-top: 15px;
}

h1 {
  position: relative;
  display: inline-block;
  font-weight: bold;
  left: 10px;
}
</style>
